import type { ApiErrorBody, AuthResponse, UserDTO } from '@va/shared';
import { desktop } from '../platform';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

export type LoginResult = { ok: true; user: UserDTO } | { ok: false; mfaTicket: string };

type Listener = (user: UserDTO | null) => void;

function toError(status: number, data: unknown): ApiError {
  const body = (data ?? {}) as Partial<ApiErrorBody>;
  return new ApiError(
    status,
    body.error ?? (status === 0 ? 'network' : 'error'),
    body.message ?? (status === 0 ? "Can't reach the store. Check your connection." : 'Something went wrong.'),
    body.details,
  );
}

interface Backend {
  request<T>(method: string, path: string, body?: unknown): Promise<T>;
  restore(): Promise<UserDTO | null>;
  login(email: string, password: string): Promise<LoginResult>;
  loginMfa(ticket: string, code: string): Promise<UserDTO>;
  register(input: { email: string; password: string; displayName?: string; acceptTerms: boolean }): Promise<UserDTO>;
  logout(): Promise<void>;
  subscribe(cb: Listener): () => void;
}

/** Browser: short-lived access token in memory, refresh token in an httpOnly cookie. */
class WebBackend implements Backend {
  private accessToken: string | null = null;
  private accessExp = 0;
  private refreshing: Promise<boolean> | null = null;
  private listeners = new Set<Listener>();

  private emit(user: UserDTO | null) {
    this.listeners.forEach((l) => l(user));
  }

  private apply(res: AuthResponse): UserDTO {
    this.accessToken = res.accessToken;
    this.accessExp = Date.parse(res.accessTokenExpiresAt);
    this.emit(res.user);
    return res.user;
  }

  private clear() {
    const had = this.accessToken !== null;
    this.accessToken = null;
    this.accessExp = 0;
    if (had) this.emit(null);
  }

  private refresh(): Promise<boolean> {
    this.refreshing ??= (async () => {
      try {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
          headers: { 'X-VA-Client': 'web', 'Content-Type': 'application/json' },
          body: '{}',
        });
        if (!res.ok) {
          this.clear();
          return false;
        }
        this.apply((await res.json()) as AuthResponse);
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => (this.refreshing = null), 0);
      }
    })();
    return this.refreshing;
  }

  async request<T>(method: string, path: string, body?: unknown, retry = true): Promise<T> {
    if (this.accessToken && Date.now() > this.accessExp - 30_000) await this.refresh();
    const headers: Record<string, string> = { 'X-VA-Client': 'web' };
    let payload: BodyInit | undefined;
    if (body instanceof FormData) payload = body;
    else if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
      payload = JSON.stringify(body);
    }
    if (this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`;
    let res: Response;
    try {
      res = await fetch(`/api${path}`, { method, headers, body: payload, credentials: 'include' });
    } catch {
      throw toError(0, null);
    }
    if (res.status === 401 && retry && this.accessToken && (await this.refresh())) {
      return this.request<T>(method, path, body, false);
    }
    const text = await res.text();
    let data: unknown = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = text;
    }
    if (!res.ok) throw toError(res.status, data);
    return data as T;
  }

  async restore(): Promise<UserDTO | null> {
    const ok = await this.refresh();
    if (!ok) return null;
    return this.request<UserDTO>('GET', '/me');
  }

  async login(email: string, password: string): Promise<LoginResult> {
    const res = await this.request<AuthResponse | { mfaRequired: true; mfaTicket: string }>('POST', '/auth/login', { email, password, client: 'web' });
    if ('mfaRequired' in res) return { ok: false, mfaTicket: res.mfaTicket };
    return { ok: true, user: this.apply(res) };
  }

  async loginMfa(ticket: string, code: string): Promise<UserDTO> {
    return this.apply(await this.request<AuthResponse>('POST', '/auth/login/mfa', { mfaTicket: ticket, code, client: 'web' }));
  }

  async register(input: { email: string; password: string; displayName?: string; acceptTerms: boolean }): Promise<UserDTO> {
    return this.apply(await this.request<AuthResponse>('POST', '/auth/register', { ...input, client: 'web' }));
  }

  async logout(): Promise<void> {
    try {
      await this.request('POST', '/auth/logout', {});
    } finally {
      this.clear();
      this.emit(null);
    }
  }

  subscribe(cb: Listener): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }
}

/** Desktop: the Electron main process owns the tokens; the UI never sees the refresh token. */
class DesktopBackend implements Backend {
  constructor(private bridge: NonNullable<typeof desktop>) {}

  async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await this.bridge.request<T>(method, path, body);
    if (!res.ok) throw toError(res.status, res.data);
    return res.data;
  }

  restore(): Promise<UserDTO | null> {
    return this.bridge.auth.session();
  }

  async login(email: string, password: string): Promise<LoginResult> {
    const res = await this.bridge.auth.login(email, password);
    if (!res.ok) throw toError(res.status, res.data);
    const data = res.data as { user?: UserDTO; mfaRequired?: boolean; mfaTicket?: string };
    if (data.mfaRequired && data.mfaTicket) return { ok: false, mfaTicket: data.mfaTicket };
    return { ok: true, user: data.user! };
  }

  async loginMfa(ticket: string, code: string): Promise<UserDTO> {
    const res = await this.bridge.auth.loginMfa(ticket, code);
    if (!res.ok) throw toError(res.status, res.data);
    return (res.data as { user: UserDTO }).user;
  }

  async register(input: { email: string; password: string; displayName?: string; acceptTerms: boolean }): Promise<UserDTO> {
    const res = await this.bridge.auth.register(input);
    if (!res.ok) throw toError(res.status, res.data);
    return (res.data as { user: UserDTO }).user;
  }

  logout(): Promise<void> {
    return this.bridge.auth.logout();
  }

  subscribe(cb: Listener): () => void {
    return this.bridge.auth.onChange(cb);
  }
}

export const backend: Backend = desktop ? new DesktopBackend(desktop) : new WebBackend();

export const api = {
  get: <T>(path: string) => backend.request<T>('GET', path),
  post: <T>(path: string, body: unknown = {}) => backend.request<T>('POST', path, body),
  patch: <T>(path: string, body: unknown = {}) => backend.request<T>('PATCH', path, body),
  del: <T>(path: string) => backend.request<T>('DELETE', path),
  upload: <T>(path: string, form: FormData) => backend.request<T>('POST', path, form),
};

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong.';
}
